import type {
  AbsChord,
  Answer,
  Pc,
  Quality,
  ScaleId,
  Seventh,
} from '@astrasonora/annotation-schema'
import { NATURAL } from '@astrasonora/annotation-schema'
import { mod12, scaleOf } from './scales.ts'
import { romanDegree } from './sky.ts'

/**
 * Absolute chord from a Roman symbol relative to a tonic, counted from its major scale (spec §1):
 * `I`, `vi`, `V7`, `♭VII`, `iv`, `V/V`, `V7/vi`, `ii°`, `♯iv°`, `IVmaj7`, `iiø7`. Case gives the
 * quality, `°`/`ø` diminished, sevenths as written. The inverse of `labelChord` for the symbols
 * the app uses; `scale` decides only whether a `V/x` whose triad is diatonic carries its seventh.
 */
export function chordFromSymbol(symbol: string, tonic: Pc, scale: ScaleId = 'ionian'): AbsChord {
  const S = scaleOf(scale)
  const sec = /^V(7)?\/(.+)$/.exec(symbol)
  if (sec) {
    const target = chordFromSymbol(sec[2]!, tonic, scale)
    const root = mod12(target.root + 7)
    // a dominant whose triad is diatonic (V/IV in major, V/♭VI in minor) is only a
    // dominant with its seventh; without it the chord is simply the diatonic chord
    const triadDiatonic = [0, 4, 7].every((t) => S.includes(mod12(root + t - tonic)))
    const seventh: Seventh = sec[1] || triadDiatonic ? 'min7' : 'none'
    return { root, quality: 'maj', seventh, bass: root }
  }
  const m = /^([♭♯]?)([IViv]+)(°|ø)?(maj7|7)?$/.exec(symbol)
  if (!m) throw new Error(`cannot parse chord symbol ${symbol}`)
  const acc = m[1] === '♭' ? -1 : m[1] === '♯' ? 1 : 0
  const num = m[2]!
  const degree = romanDegree(num)
  if (degree === undefined) throw new Error(`bad numeral in ${symbol}`)
  const root = mod12(tonic + NATURAL[degree]! + acc)
  const upper = num === num.toUpperCase()
  let quality: Quality = upper ? 'maj' : 'min'
  if (m[3]) quality = 'dim'
  let seventh: Seventh = 'none'
  if (m[4] === 'maj7') seventh = 'maj7'
  else if (m[4] === '7') seventh = quality === 'dim' && m[3] === '°' ? 'dim7' : 'min7'
  return { root, quality, seventh, bass: root }
}

/** The chord a player's answer names: the triad of its cell on the song's tonic. */
export function chordFromAnswer(a: Answer, tonic: Pc): AbsChord {
  const root = mod12(tonic + a.rel)
  return { root, quality: a.ring, seventh: 'none', bass: root }
}
